import { RCAutocomplete, RCFormGroup } from '@reverbdotcom/cadence/components';
import I18n from 'i18n-js';
import { RCAutocompleteItem } from '@reverbdotcom/cadence/components/RCAutocomplete/RCAutocompleteItem';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import gql from 'graphql-tag';
import React from 'react';
import { DataValue } from 'react-apollo';
import {
  CoreAddressAutocompleteFindAddressSuggestions,
  CoreAddressAutocompleteSelectAddressSuggestion,
  core_apimessages_AddressEntry as AddressEntry,
  rql_AddressSuggestion as AddressSuggestion,
  rql_AddressSuggestion_Type as AddressSuggestionType,
} from '../schemas/rql';
import { connect } from '@reverbdotcom/commons/src/connect';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

interface QueryProps {
  selectData: DataValue<CoreAddressAutocompleteSelectAddressSuggestion.Query, CoreAddressAutocompleteSelectAddressSuggestion.Variables>;
  suggestionsData: DataValue<CoreAddressAutocompleteFindAddressSuggestions.Query, CoreAddressAutocompleteFindAddressSuggestions.Variables>;
}

interface ExternalProps {
  onAddressSelect(address: AddressEntry): void;
  onChooseManualEntry(): void;
}

type Props = ExternalProps & QueryProps;

export function AddressAutocomplete(props: Props) {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [inputValue, setInputValue] = React.useState('');
  const suggestions = props.suggestionsData.findAddressSuggestions?.suggestions || [];
  const showManualEntry = inputValue.length > 2 || suggestions.length > 1;
  const user = useUser();

  function onQueryChange(value: string) {
    setInputValue(value);
    props.suggestionsData.refetch({ query: value, containerId: null, countryCode: user.countryCode, skip: false });
  }

  async function onSuggestionSelect(suggestion: AddressSuggestion) {
    if (suggestion.type === AddressSuggestionType.CONTAINER) {
      props.suggestionsData.refetch({ containerId: suggestion.id, countryCode: user.countryCode, skip: false });
    } else if (suggestion.type === AddressSuggestionType.ADDRESS) {
      const result = await props.selectData.refetch({ id: suggestion.id, skip: false });
      const address = result.data?.selectAddressSuggestion?.address;

      if (address) {
        setIsOpen(false);
        props.onAddressSelect(address);
      }
    }
  }

  return (
    <RCFormGroup
      inputId="address-autocomplete-input"
      label={I18n.t('discovery.addressAutocomplete.label')}
    >
      <RCAutocomplete
        inputValue={inputValue}
        inputId="address-autocomplete-input"
        onInputChange={onQueryChange}
        loading={props.suggestionsData?.loading}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        placeholder={I18n.t('discovery.addressAutocomplete.placeholder')}
      >
        {inputValue.length !== 0 &&
          <RCAutocompleteItem
            key="keep-typing"
            id="keep-typing"
            title={I18n.t('discovery.addressAutocomplete.continueTyping')}
            variant="disabled"
          />
        }
        {suggestions.length > 0 &&
          suggestions.map((suggestion) => (
            <RCAutocompleteItem
              key={suggestion.id}
              id={suggestion.id}
              title={suggestion.text}
              type={suggestion.type}
              onClick={() => onSuggestionSelect(suggestion)}
              variant={suggestion.type === AddressSuggestionType.CONTAINER ? 'parent' : undefined}
            />
          ))
        }
        {showManualEntry &&
          <RCAutocompleteItem
            key="manual-entry"
            id="manual-entry"
            title={I18n.t('discovery.addressAutocomplete.manualEntry')}
            variant="link"
            onClick={() => props.onChooseManualEntry()}
          />
        }
      </RCAutocomplete>
    </RCFormGroup>
  );
}

const withFindSuggestionsQuery = withGraphql<
  ExternalProps,
  CoreAddressAutocompleteFindAddressSuggestions.Query,
  CoreAddressAutocompleteFindAddressSuggestions.Variables
>(
  gql`
    query Core_AddressAutocomplete_FindAddressSuggestions(
      $query: String,
      $containerId: String,
      $countryCode: String,
      $skip: Boolean!
    ) {
      findAddressSuggestions(input: { text: $query, container: $containerId, countryCode: $countryCode }) @skip(if: $skip) {
        suggestions {
          id
          type
          text
        }
      }
    }
  `,
  {
    name: 'suggestionsData',
    options: () => ({
      variables: {
        skip: true,
      },
    }),
  },
);


const withSelectSuggestionQuery = withGraphql<
  ExternalProps,
  CoreAddressAutocompleteSelectAddressSuggestion.Query,
  CoreAddressAutocompleteSelectAddressSuggestion.Variables
>(gql`
  query Core_AddressAutocomplete_SelectAddressSuggestion(
    $id: String,
    $skip: Boolean!
  ) {
    selectAddressSuggestion(input: { id: $id }) @skip(if: $skip) {
      id
      address {
        streetAddress
        extendedAddress
        locality
        region
        postalCode
        countryCode
      }
    }
  }
  `, {
  name: 'selectData',
  options: () => ({
    variables: {
      skip: true,
    },
  }),
},
);

export default connect<ExternalProps>([
  withFindSuggestionsQuery,
  withSelectSuggestionQuery,
])(AddressAutocomplete);
